import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './AdminInquiries.scss';

const DEFAULT_FILTERS = {
  type: 'all',
  status: 'all',
  unreadOnly: false
};

export default function AdminInquiries() {
  const navigate = useNavigate();

  const apiBaseUrl = useMemo(() => {
    const envUrl = process.env.REACT_APP_API_URL || '';
    return envUrl.endsWith('/') ? envUrl.slice(0, -1) : envUrl;
  }, []);

  const token = localStorage.getItem('tg_admin_token');
  const storedUser = localStorage.getItem('tg_admin_user');

  const [adminUser, setAdminUser] = useState(storedUser ? JSON.parse(storedUser) : null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [panelLoading, setPanelLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState({
    loading: false,
    success: '',
    error: ''
  });

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }

    const validateAdmin = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/admin/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.message || 'Session expired.');
        }

        localStorage.setItem('tg_admin_user', JSON.stringify(data.adminUser));
        setAdminUser(data.adminUser);
      } catch (err) {
        localStorage.removeItem('tg_admin_token');
        localStorage.removeItem('tg_admin_user');
        navigate('/admin');
      }
    };

    validateAdmin();
  }, [apiBaseUrl, navigate, token]);

  useEffect(() => {
    if (!token) {
      return;
    }

    fetchInquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, filters, token]);

  const fetchInquiries = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();

      if (filters.type !== 'all') {
        params.append('type', filters.type);
      }

      if (filters.status !== 'all') {
        params.append('status', filters.status);
      }

      if (filters.unreadOnly) {
        params.append('unreadOnly', 'true');
      }

      params.append('limit', '100');

      const response = await fetch(`${apiBaseUrl}/admin/inquiries?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Unable to load inquiries.');
      }

      const nextInquiries = data.inquiries || [];
      setInquiries(nextInquiries);

      if (nextInquiries.length === 0) {
        setSelectedInquiry(null);
        return;
      }

      setSelectedInquiry((current) => {
        if (!current) {
          return nextInquiries[0];
        }

        const stillExists = nextInquiries.find(
          (item) => item.inquiry_id === current.inquiry_id
        );

        return stillExists || nextInquiries[0];
      });
    } catch (err) {
      setError(err.message || 'Something went wrong loading inquiries.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('tg_admin_token');
    localStorage.removeItem('tg_admin_user');
    navigate('/admin');
  };

  const handleFilterChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFilters((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    setSelectedInquiry(null);
  };

  const handleSelectInquiry = async (inquiryId) => {
    setPanelLoading(true);
    setSaveStatus({
      loading: false,
      success: '',
      error: ''
    });

    try {
      const response = await fetch(`${apiBaseUrl}/admin/inquiries/${inquiryId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Unable to load inquiry.');
      }

      setSelectedInquiry(data.inquiry);
    } catch (err) {
      setSaveStatus({
        loading: false,
        success: '',
        error: err.message || 'Unable to load inquiry details.'
      });
    } finally {
      setPanelLoading(false);
    }
  };

  const handleSelectedFieldChange = (event) => {
    const { name, value, type, checked } = event.target;

    setSelectedInquiry((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveSelectedInquiry = async () => {
    if (!selectedInquiry) {
      return;
    }

    setSaveStatus({
      loading: true,
      success: '',
      error: ''
    });

    try {
      const response = await fetch(`${apiBaseUrl}/admin/inquiries/${selectedInquiry.inquiry_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          isRead: selectedInquiry.is_read,
          status: selectedInquiry.status,
          adminNotes: selectedInquiry.admin_notes || ''
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Unable to save inquiry.');
      }

      setSelectedInquiry(data.inquiry);

      setInquiries((prev) =>
        prev.map((item) =>
          item.inquiry_id === data.inquiry.inquiry_id ? data.inquiry : item
        )
      );

      setSaveStatus({
        loading: false,
        success: 'Inquiry updated successfully.',
        error: ''
      });
    } catch (err) {
      setSaveStatus({
        loading: false,
        success: '',
        error: err.message || 'Unable to save inquiry.'
      });
    }
  };

  return (
    <div className="admin-inquiries-page">
      <header className="admin-inquiries-header">
        <div>
          <p className="admin-inquiries-header__eyebrow">The Tour Golfers Admin</p>
          <h1>Inquiries</h1>
          <p className="admin-inquiries-header__subtext">
            Review invite requests, contact messages, and partnership submissions.
          </p>
        </div>

        <div className="admin-inquiries-header__actions">
          {adminUser ? (
            <span className="admin-inquiries-header__user">
              {adminUser.fullName || adminUser.email}
            </span>
          ) : null}

          <Link className="admin-nav-link" to="/admin/reports">
            Moderation Reports
          </Link>

          <button type="button" onClick={handleLogout}>
            Log Out
          </button>
        </div>
      </header>

      <section className="admin-filters">
        <div className="admin-filters__field">
          <label htmlFor="type">Inquiry Type</label>
          <select id="type" name="type" value={filters.type} onChange={handleFilterChange}>
            <option value="all">All</option>
            <option value="invite">Invite</option>
            <option value="contact">Contact</option>
            <option value="partnership">Partnership</option>
          </select>
        </div>

        <div className="admin-filters__field">
          <label htmlFor="status">Status</label>
          <select id="status" name="status" value={filters.status} onChange={handleFilterChange}>
            <option value="all">All</option>
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="replied">Replied</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <label className="admin-filters__checkbox">
          <input
            type="checkbox"
            name="unreadOnly"
            checked={filters.unreadOnly}
            onChange={handleFilterChange}
          />
          Unread only
        </label>
      </section>

      {error ? <div className="admin-page-error">{error}</div> : null}

      <div className="admin-inquiries-layout">
        <section className="admin-inquiries-list">
          {loading ? (
            <div className="admin-inquiries-list__empty">Loading inquiries...</div>
          ) : inquiries.length === 0 ? (
            <div className="admin-inquiries-list__empty">
              No inquiries match the current filters.
            </div>
          ) : (
            inquiries.map((inquiry) => (
              <button
                key={inquiry.inquiry_id}
                type="button"
                className={`inquiry-list-item ${
                  selectedInquiry?.inquiry_id === inquiry.inquiry_id ? 'is-active' : ''
                }`}
                onClick={() => handleSelectInquiry(inquiry.inquiry_id)}
              >
                <div className="inquiry-list-item__top">
                  <span className={`inquiry-pill inquiry-pill--${inquiry.inquiry_type}`}>
                    {inquiry.inquiry_type}
                  </span>
                  {!inquiry.is_read ? <span className="inquiry-unread-dot"></span> : null}
                </div>

                <h3>{inquiry.full_name}</h3>
                <p>{inquiry.email}</p>
                <p>{inquiry.city || 'No city provided'}</p>

                <div className="inquiry-list-item__bottom">
                  <span className={`status-pill status-pill--${inquiry.status}`}>
                    {inquiry.status}
                  </span>
                  <span>{new Date(inquiry.created_at).toLocaleString()}</span>
                </div>
              </button>
            ))
          )}
        </section>

        <section className="admin-inquiry-panel">
          {panelLoading ? (
            <div className="admin-inquiry-panel__empty">Loading inquiry...</div>
          ) : !selectedInquiry ? (
            <div className="admin-inquiry-panel__empty">
              Select an inquiry to view details.
            </div>
          ) : (
            <>
              <div className="admin-inquiry-panel__header">
                <div>
                  <p className="admin-inquiry-panel__eyebrow">
                    {selectedInquiry.inquiry_type} inquiry
                  </p>
                  <h2>{selectedInquiry.full_name}</h2>
                </div>

                <span className={`status-pill status-pill--${selectedInquiry.status}`}>
                  {selectedInquiry.status}
                </span>
              </div>

              <div className="admin-inquiry-panel__meta">
                <div>
                  <strong>Email</strong>
                  <span>{selectedInquiry.email}</span>
                </div>

                <div>
                  <strong>City</strong>
                  <span>{selectedInquiry.city || '—'}</span>
                </div>

                <div>
                  <strong>Source</strong>
                  <span>{selectedInquiry.source_page || '—'}</span>
                </div>

                <div>
                  <strong>Submitted</strong>
                  <span>{new Date(selectedInquiry.created_at).toLocaleString()}</span>
                </div>
              </div>

              <div className="admin-inquiry-panel__message">
                <strong>Message</strong>
                <p>{selectedInquiry.message}</p>
              </div>

              <div className="admin-inquiry-panel__controls">
                <label className="admin-inquiry-panel__checkbox">
                  <input
                    type="checkbox"
                    name="is_read"
                    checked={selectedInquiry.is_read}
                    onChange={handleSelectedFieldChange}
                  />
                  Mark as read
                </label>

                <div className="admin-inquiry-panel__field">
                  <label htmlFor="statusSelect">Status</label>
                  <select
                    id="statusSelect"
                    name="status"
                    value={selectedInquiry.status}
                    onChange={handleSelectedFieldChange}
                  >
                    <option value="new">New</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="replied">Replied</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                <div className="admin-inquiry-panel__field">
                  <label htmlFor="adminNotes">Admin Notes</label>
                  <textarea
                    id="adminNotes"
                    name="admin_notes"
                    rows="6"
                    value={selectedInquiry.admin_notes || ''}
                    onChange={handleSelectedFieldChange}
                    placeholder="Add internal notes here..."
                  />
                </div>

                {saveStatus.success ? (
                  <div className="admin-save-status admin-save-status--success">
                    {saveStatus.success}
                  </div>
                ) : null}

                {saveStatus.error ? (
                  <div className="admin-save-status admin-save-status--error">
                    {saveStatus.error}
                  </div>
                ) : null}

                <button
                  type="button"
                  className="admin-save-button"
                  onClick={handleSaveSelectedInquiry}
                  disabled={saveStatus.loading}
                >
                  {saveStatus.loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}